import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import withTranslate from 'containers/widgets/withTranslate';
import Button from 'components/atoms/Button';
import CopyToClipboardButton from 'components/molecules/CopyToClipboardButton';
import Icon, { IconSizes, IconVariants } from 'components/foundations/Icon';
import {
    REFERENCE_LINK_DESCRIPTION_MAX_LENGTH,
    REFERENCE_LINK_URL_MAX_LENGTH,
    REFERENCE_LINKS_MAX_COUNT,
    REFERENCE_LINKS_PREFIX,
} from '../constants';
import { isAtMaxLinks, parseLinks, validateRows } from '../utils/referenceLinks';
import useReferenceLinkAccess from '../hooks/useReferenceLinkAccess';
import ReferenceLinkStatusChip from './ReferenceLinkStatusChip';
import css from './ReferenceLinksField.css';

const emptyRow = () => ({ url: '', description: '' });

const ReferenceLinksField = ({
    question,
    value,
    serviceAccountEmail = null,
    onChange = () => {},
    readOnly = false,
    translate,
}) => {
    const parsedRows = useMemo(() => parseLinks(value), [value]);
    const rows = useMemo(
        () => (!readOnly && parsedRows.length === 0 ? [emptyRow()] : parsedRows),
        [parsedRows, readOnly]
    );
    const rowErrors = useMemo(() => validateRows(rows), [rows]);
    const { statusFor, isVerifying, checkAccess } = useReferenceLinkAccess(translate);

    // Preserve blank rows in the parent value so the user can keep editing them.
    // Filtering happens server-side and in export/answered helpers.
    const emit = nextRows => {
        onChange(question.id, nextRows.length === 0 ? '' : JSON.stringify(nextRows));
    };

    const handleAdd = () => {
        if (readOnly || isAtMaxLinks(rows)) return;
        emit([...rows, emptyRow()]);
    };

    const handleRemove = index => {
        if (readOnly) return;
        emit(rows.filter((_, i) => i !== index));
    };

    const handleFieldChange = (index, field, fieldValue) => {
        if (readOnly) return;
        const next = rows.map((row, i) => (i === index ? { ...row, [field]: fieldValue } : row));
        emit(next);
    };

    const renderReadOnlyRows = () => (
        <div className={css.readOnlyList}>
            {rows.length === 0 && <div className={css.empty}>{translate(`${REFERENCE_LINKS_PREFIX}.EMPTY`)}</div>}
            {rows.map((row, index) => (
                <div key={index} className={css.readOnlyRow}>
                    <a className={css.readOnlyUrl} href={row.url} target="_blank" rel="noopener noreferrer">
                        {row.url}
                    </a>
                    {row.description && <div className={css.readOnlyDescription}>{row.description}</div>}
                </div>
            ))}
        </div>
    );

    if (readOnly) {
        return (
            <div className={css.field}>
                <div className={css.header}>
                    <div className={css.title}>{translate(question.title)}</div>
                    <div className={css.description}>{translate(question.description)}</div>
                </div>
                {renderReadOnlyRows()}
            </div>
        );
    }

    const atMax = isAtMaxLinks(rows);

    return (
        <div className={css.field}>
            <div className={css.header}>
                <div className={css.title}>{translate(question.title)}</div>
                <div className={css.description}>{translate(question.description)}</div>
                {serviceAccountEmail && (
                    <div className={css.shareHint}>
                        <span>
                            {translate(`${REFERENCE_LINKS_PREFIX}.SHARE_HINT_PREFIX`)}
                            <span className={css.shareHintEmphasis}>
                                {translate(`${REFERENCE_LINKS_PREFIX}.SHARE_HINT_VIEWER`)}
                            </span>
                            {translate(`${REFERENCE_LINKS_PREFIX}.SHARE_HINT_SUFFIX`)}
                        </span>
                        <div className={css.shareEmail}>
                            <span className={css.shareEmailText}>{serviceAccountEmail}</span>
                            <CopyToClipboardButton
                                textToCopy={serviceAccountEmail}
                                tooltip={`${REFERENCE_LINKS_PREFIX}.COPY_EMAIL`}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className={css.rows}>
                {rows.map((row, index) => {
                    const errors = rowErrors[index] || {};
                    const isRowPopulated = (row.url || '').length > 0 || (row.description || '').length > 0;
                    const showUrlError = !!errors.url && isRowPopulated;
                    const showDescriptionError = !!errors.description && isRowPopulated;
                    const trimmedUrl = (row.url || '').trim();
                    const canCheckAccess = !!trimmedUrl && !errors.url;
                    return (
                        <div key={index} className={css.rowWrapper}>
                            <div className={css.row}>
                                <div className={css.inputs}>
                                    <div className={classNames(css.inputGroup, css.urlGroup)}>
                                        <input
                                            className={classNames(css.input, {
                                                [css.inputError]: showUrlError,
                                            })}
                                            type="text"
                                            value={row.url}
                                            placeholder={translate(`${REFERENCE_LINKS_PREFIX}.URL_PLACEHOLDER`)}
                                            maxLength={REFERENCE_LINK_URL_MAX_LENGTH}
                                            onChange={e => handleFieldChange(index, 'url', e.target.value)}
                                        />
                                        {showUrlError && <div className={css.error}>{translate(errors.url)}</div>}
                                    </div>
                                    <div className={classNames(css.inputGroup, css.descriptionGroup)}>
                                        <input
                                            className={classNames(css.input, {
                                                [css.inputError]: showDescriptionError,
                                            })}
                                            type="text"
                                            value={row.description}
                                            placeholder={translate(`${REFERENCE_LINKS_PREFIX}.DESCRIPTION_PLACEHOLDER`)}
                                            maxLength={REFERENCE_LINK_DESCRIPTION_MAX_LENGTH}
                                            onChange={e => handleFieldChange(index, 'description', e.target.value)}
                                        />
                                        {showDescriptionError && (
                                            <div className={classNames(css.error, css.descriptionError)}>
                                                {translate(errors.description)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={css.removeButton}
                                    onClick={() => handleRemove(index)}
                                    aria-label={translate(`${REFERENCE_LINKS_PREFIX}.REMOVE_LINK`)}
                                    title={translate(`${REFERENCE_LINKS_PREFIX}.REMOVE_LINK`)}
                                >
                                    <Icon
                                        name="trash"
                                        size={IconSizes.SMALL}
                                        variant={IconVariants.LIGHT}
                                        interactive={false}
                                    />
                                </button>
                            </div>
                            <div className={css.rowFooter}>
                                <Button
                                    type="flat"
                                    Icon="check"
                                    onClick={() => checkAccess(trimmedUrl)}
                                    showSpinner={isVerifying(trimmedUrl)}
                                    disabled={!canCheckAccess || isVerifying(trimmedUrl)}
                                >
                                    {translate(`${REFERENCE_LINKS_PREFIX}.CHECK_ACCESS`)}
                                </Button>
                                <ReferenceLinkStatusChip
                                    status={statusFor(trimmedUrl)}
                                    serviceAccountEmail={serviceAccountEmail}
                                    translate={translate}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className={css.addRow}>
                <Button type="flat" Icon="plus" onClick={handleAdd} disabled={atMax}>
                    {translate(`${REFERENCE_LINKS_PREFIX}.ADD_LINK`)}
                </Button>
                <span className={css.counter}>
                    {translate(`${REFERENCE_LINKS_PREFIX}.COUNTER`, {
                        count: rows.length,
                        max: REFERENCE_LINKS_MAX_COUNT,
                    })}
                </span>
            </div>
        </div>
    );
};

ReferenceLinksField.propTypes = {
    question: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
    }).isRequired,
    value: PropTypes.string,
    serviceAccountEmail: PropTypes.string,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withTranslate(ReferenceLinksField);
